import React from 'react';
import { t } from '@lingui/core/macro';

import AutocompleteMultiSelect from '@rover/components/src/formFields/AutocompleteMultiSelect';
import { Text } from '@rover/kibble/core';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import { useBreeds } from '@rover/react-lib/src/hooks/useBreeds';
import type { Breed } from '@rover/rsdk/src/apiClient/latest/model/breed';
import { PetType } from '@rover/types/src/Pet';

const isWolfDogBreed = (breeds: string[], dogBreeds: Breed[]): boolean =>
  breeds.some((name) =>
    dogBreeds
      .filter((b) => b.name === name)
      .some((b) => b.slug === 'kunming-wolfdog' || b.slug === 'czechoslovak-wolfdog')
  );

export const AutoCompleteBreeds: React.FC<{
  values: string[];
  error?: string | undefined | string | null;
  onChange: (values: string[], hasWolfBreed: boolean) => void;
  onBlur?: () => void;
  petType: PetType;
  showSubLabel?: boolean;
  hideTitle?: boolean;
}> = ({
  values,
  onChange,
  onBlur,
  error,
  petType,
  showSubLabel = false,
  hideTitle = false,
}): JSX.Element => {
  const { breeds } = useBreeds(petType);
  const subLabel =
    !error &&
    showSubLabel &&
    t`Enter all breeds that apply. If your dog is a mixed breed, add ‘Mixed’ as well.`;

  return (
    <LabelAndErrorFormGroup
      name="breeds"
      label={hideTitle ? undefined : <Text size="100">{t`Breed(s)`}</Text>}
      subLabelPosition="below"
      placeholder=""
      subLabel={subLabel}
      validationError={error}
      validationType="inline"
      InputComponent={AutocompleteMultiSelect}
      useKibbleStandardSizeStyle
      // @ts-expect-error LabelAndErrorFormGroup is not typed correctly
      options={breeds.map((breed) => ({
        title: breed.name,
        value: breed.name,
        id: breed.slug,
      }))}
      hasError={!!error}
      selectedValues={values}
      onSelectOption={(value): void => {
        const breedNames = [...values, value];
        onChange(breedNames, isWolfDogBreed(breedNames, breeds));
      }}
      onUnselectOption={(value): void => {
        const breedNames = values.filter((val) => value !== val);
        onChange(breedNames, isWolfDogBreed(breedNames, breeds));
      }}
      onBlur={onBlur}
    />
  );
};
